import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import { generateDeliveryChallanPDF } from '../utils/pdfGenerator';
import { Package, Printer, CheckCircle, XCircle, Truck, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ParcelManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [pendingParcels, setPendingParcels] = useState([]);
  const [challans, setChallans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'challans'

  // Challan creation dialog
  const [createChallanDialogOpen, setCreateChallanDialogOpen] = useState(false);
  const [selectedWorkerGroup, setSelectedWorkerGroup] = useState(null);
  const [selectedMaterialRequests, setSelectedMaterialRequests] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [manualWorkerSelection, setManualWorkerSelection] = useState(null); // For unassigned materials

  // Transport details
  const [transporter, setTransporter] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [lrAwbNumber, setLrAwbNumber] = useState('');
  const [ewayBillNumber, setEwayBillNumber] = useState('');
  const [challanNotes, setChallanNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'pending') {
        await loadPendingParcels();
      } else {
        await loadChallans();
      }
      await loadWorkers();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingParcels = async () => {
    try {
      const response = await api.get('/challans/pending-parcels');
      // Group by WORKER (not order)
      const grouped = {};
      response.data.forEach(req => {
        // Ensure req has proper _id field
        if (!req._id && !req.id) {
          console.error('Material request missing ID:', req);
          return;
        }

        // Use _id or id field
        const reqId = req._id || req.id;
        req.id = reqId;  // Normalize to id field

        const workerId = req.worker_id || 'unassigned';
        const workerName = req.worker_name || 'Unassigned';

        if (!grouped[workerId]) {
          grouped[workerId] = {
            worker_id: workerId,
            worker_name: workerName,
            worker_mobile: null,
            requests: [],
            is_unassigned: workerId === 'unassigned'
          };
        }
        grouped[workerId].requests.push(req);
      });

      // Fetch worker mobile numbers for assigned workers only
      for (const group of Object.values(grouped)) {
        if (group.worker_id && group.worker_id !== 'unassigned') {
          try {
            const worker = workers.find(w => w.id === group.worker_id);
            if (worker) {
              group.worker_mobile = worker.mobile_number;
            }
          } catch (error) {
            console.error('Error fetching worker details:', error);
          }
        }
      }

      // Sort: Assigned workers first, then Unassigned
      const sorted = Object.values(grouped).sort((a, b) => {
        if (a.is_unassigned && !b.is_unassigned) return 1;
        if (!a.is_unassigned && b.is_unassigned) return -1;
        return 0;
      });

      setPendingParcels(sorted);
    } catch (error) {
      console.error('Error loading pending parcels:', error);
    }
  };

  const loadChallans = async () => {
    try {
      console.log('Loading challans...');
      const response = await api.get('/challans/');
      console.log('Challans loaded:', response.data.length);
      setChallans(response.data || []);
    } catch (error) {
      console.error('Error loading challans:', error);
      toast({
        title: 'Error',
        description: 'Failed to load challans',
        variant: 'destructive',
      });
    }
  };

  const loadWorkers = async () => {
    try {
      const response = await api.get('/users/by-role/worker');
      setWorkers(response.data);
    } catch (error) {
      console.error('Error loading workers:', error);
    }
  };

  const openCreateChallanDialog = (workerGroup) => {
    setSelectedWorkerGroup(workerGroup);
    // DON'T reset selectedMaterialRequests - keep user's checkbox selections
    // Only set if nothing selected yet (edge case)
    if (selectedMaterialRequests.length === 0) {
      setSelectedMaterialRequests(workerGroup.requests.map(r => r.id));
    }
    // Reset manual worker selection
    setManualWorkerSelection(null);
    setCreateChallanDialogOpen(true);
  };

  const toggleMaterialRequest = (requestId) => {
    if (selectedMaterialRequests.includes(requestId)) {
      setSelectedMaterialRequests(selectedMaterialRequests.filter(id => id !== requestId));
    } else {
      setSelectedMaterialRequests([...selectedMaterialRequests, requestId]);
    }
  };

  const calculateSelectedTotal = () => {
    if (!selectedWorkerGroup) return 0;
    return selectedWorkerGroup.requests
      .filter(req => selectedMaterialRequests.includes(req.id))
      .reduce((sum, req) => {
        const insurance = req.partial_issuances?.reduce((total, iss) =>
          iss.needs_parcel && iss.insurance_value ? total + iss.insurance_value : total, 0) || 0;
        return sum + insurance;
      }, 0);
  };

  const handleCreateChallan = async () => {
    // For unassigned group, worker selection is required
    const isUnassigned = !selectedWorkerGroup.worker_id || selectedWorkerGroup.worker_id === 'unassigned';

    if (isUnassigned && !manualWorkerSelection) {
      toast({
        title: 'Error',
        description: 'Please select a worker for these materials',
        variant: 'destructive'
      });
      return;
    }

    if (!selectedWorkerGroup) {
      toast({ title: 'Error', description: 'Worker information missing', variant: 'destructive' });
      return;
    }

    if (selectedMaterialRequests.length === 0) {
      toast({ title: 'Error', description: 'Please select at least one material', variant: 'destructive' });
      return;
    }

    if (!transporter) {
      toast({ title: 'Error', description: 'Please select a transporter', variant: 'destructive' });
      return;
    }

    try {
      setLoading(true);

      // Get order_id from first material request
      const firstRequest = selectedWorkerGroup.requests.find(r => selectedMaterialRequests.includes(r.id));

      // Use manual selection for unassigned, otherwise use group worker_id
      const finalWorkerId = isUnassigned ? manualWorkerSelection : selectedWorkerGroup.worker_id;

      const challanData = {
        order_id: String(firstRequest.order_id),  // Ensure string
        worker_id: String(finalWorkerId),  // Ensure string
        material_request_ids: selectedMaterialRequests.map(id => String(id)),  // Ensure all IDs are strings
        transport_details: {
          transporter_name: String(transporter),  // Ensure string
          vehicle_number: vehicleNumber && vehicleNumber.trim() ? String(vehicleNumber.trim()) : null,
          lr_awb_number: lrAwbNumber && lrAwbNumber.trim() ? String(lrAwbNumber.trim()) : null,
          eway_bill_number: ewayBillNumber && ewayBillNumber.trim() ? String(ewayBillNumber.trim()) : null,
        },
        notes: challanNotes && challanNotes.trim() ? String(challanNotes.trim()) : null,
      };

      console.log('Sending challan data:', JSON.stringify(challanData, null, 2));

      const response = await api.post('/challans/create', challanData);

      toast({
        title: 'Challan Created!',
        description: `Challan ${response.data.challan_number} created successfully`,
      });

      // Reset and close
      setCreateChallanDialogOpen(false);
      resetChallanForm();
      loadData();
    } catch (error) {
      console.error('Create challan error:', error);

      // Handle Pydantic validation errors properly
      let errorMessage = 'Failed to create challan';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetChallanForm = () => {
    setSelectedWorkerGroup(null);
    setSelectedMaterialRequests([]);
    setManualWorkerSelection(null);
    setTransporter('');
    setVehicleNumber('');
    setLrAwbNumber('');
    setEwayBillNumber('');
    setChallanNotes('');
  };

  const updateChallanStatus = async (challanId, newStatus) => {
    try {
      await api.patch(`/challans/${challanId}/status`, {
        status: newStatus,
      });

      toast({
        title: 'Status Updated',
        description: `Challan status changed to ${newStatus}`,
      });

      loadChallans();
    } catch (error) {
      console.error('Update status error:', error);

      // Handle errors properly
      let errorMessage = 'Failed to update status';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-700',
      dispatched: 'bg-blue-100 text-blue-700',
      received: 'bg-green-100 text-green-700',
      returned: 'bg-purple-100 text-purple-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-teal-700 text-white p-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-teal-600 p-2 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold truncate">Parcel Management</h1>
            <p className="text-xs text-teal-100">Manage delivery challans for job work</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="flex">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'pending'
                ? 'border-teal-600 text-teal-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            📦 Pending Parcels ({pendingParcels.length})
          </button>
          <button
            onClick={() => setActiveTab('challans')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'challans'
                ? 'border-teal-600 text-teal-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            📄 All Challans ({challans.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : activeTab === 'pending' ? (
          /* Pending Parcels View - GROUPED BY WORKER */
          <div className="space-y-4">
            {pendingParcels.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No pending parcels</p>
              </div>
            ) : (
              pendingParcels.map((workerGroup) => (
                <div key={workerGroup.worker_id} className={`rounded-lg border-2 p-4 ${
                  workerGroup.is_unassigned
                    ? 'bg-orange-50 border-orange-300'
                    : 'bg-white border-teal-200'
                }`}>
                  {/* Worker Header */}
                  <div className="flex items-start justify-between mb-3 pb-3 border-b-2 border-teal-100">
                    <div>
                      <h3 className={`font-bold text-lg flex items-center ${
                        workerGroup.is_unassigned ? 'text-orange-900' : 'text-teal-900'
                      }`}>
                        {workerGroup.is_unassigned ? '⚠️' : '👤'} {workerGroup.worker_name}
                      </h3>
                      {workerGroup.worker_mobile && (
                        <p className="text-sm text-gray-600">📱 {workerGroup.worker_mobile}</p>
                      )}
                      {workerGroup.is_unassigned && (
                        <p className="text-xs text-orange-600 mt-1">
                          ⚠️ Worker not assigned - select worker when creating challan
                        </p>
                      )}
                    </div>
                    <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-medium">
                      {workerGroup.requests.length} Material{workerGroup.requests.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Material Requests with Checkboxes */}
                  <div className="space-y-2 mb-4">
                    {workerGroup.requests.map((req) => {
                      const insurance = req.partial_issuances?.reduce((total, iss) =>
                        iss.needs_parcel && iss.insurance_value ? total + iss.insurance_value : total, 0) || 0;

                      return (
                        <label
                          key={req.id}
                          className="flex items-start space-x-3 p-3 bg-gray-50 rounded border-2 cursor-pointer hover:bg-gray-100 hover:border-teal-300 transition-all"
                        >
                          <input
                            type="checkbox"
                            checked={selectedWorkerGroup?.worker_id === workerGroup.worker_id && selectedMaterialRequests.includes(req.id)}
                            onChange={() => {
                              // If clicking from a different worker group, reset selection to this group
                              if (selectedWorkerGroup?.worker_id !== workerGroup.worker_id) {
                                setSelectedWorkerGroup(workerGroup);
                                setSelectedMaterialRequests([req.id]);
                              } else {
                                // Same worker group - toggle this material
                                toggleMaterialRequest(req.id);
                              }
                            }}
                            className="w-5 h-5 mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-medium text-base">
                                {req.material_type === 'gold' && '🥇'}
                                {req.material_type === 'diamond' && '💎'}
                                {req.material_type === 'stone' && '💠'}
                                {' '}{req.material_type?.toUpperCase() || 'N/A'}
                                {req.metal_purity && ` (${req.metal_purity})`}
                              </p>
                              <span className="text-sm font-bold text-teal-700">
                                ₹{typeof insurance === 'number' ? insurance.toLocaleString() : '0'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">
                              Order: {typeof req.order_details?.order_number === 'string'
                                ? req.order_details.order_number
                                : 'N/A'} | Request: {req.request_number || 'N/A'}
                            </p>
                            {req.partial_issuances?.map((issuance, idx) => (
                              issuance.needs_parcel && (
                                <p key={idx} className="text-xs text-gray-500 mt-1">
                                  📦 {issuance.weight_issued || 0}{req.weight_grams ? 'g' : 'ct'} | Voucher: {issuance.voucher_number || 'N/A'}
                                </p>
                              )
                            ))}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Selection Summary & Create Button */}
                  {selectedWorkerGroup?.worker_id === workerGroup.worker_id && selectedMaterialRequests.length > 0 && (
                    <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-3 mb-3">
                      <p className="text-sm font-semibold text-teal-900">
                        ✓ Selected: {selectedMaterialRequests.length} material{selectedMaterialRequests.length > 1 ? 's' : ''}
                      </p>
                      <p className="text-sm font-bold text-teal-700">
                        Total Value: ₹{calculateSelectedTotal().toLocaleString()}
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={() => openCreateChallanDialog(workerGroup)}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-base py-6"
                    disabled={selectedWorkerGroup?.worker_id === workerGroup.worker_id && selectedMaterialRequests.length === 0}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Create Challan for {workerGroup.worker_name}
                    {selectedWorkerGroup?.worker_id === workerGroup.worker_id && selectedMaterialRequests.length > 0 &&
                      ` (${selectedMaterialRequests.length} selected)`
                    }
                  </Button>
                </div>
              ))
            )}
          </div>
        ) : (
          /* All Challans View */
          <div className="space-y-4">
            {challans.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-500">No challans created yet</p>
              </div>
            ) : (
              challans.map((challan) => (
                <div key={challan.id} className="bg-white rounded-lg border p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg">{challan.challan_number}</h3>
                      <p className="text-sm text-gray-600">
                        Order: {challan.order_number} | Worker: {challan.worker_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(challan.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(challan.status)}`}>
                      {challan.status.toUpperCase()}
                    </span>
                  </div>

                  {/* Materials Summary */}
                  <div className="bg-gray-50 p-3 rounded mb-3">
                    <p className="text-sm font-medium mb-2">Materials ({Array.isArray(challan.material_requests_data) ? challan.material_requests_data.length : 0}):</p>
                    {Array.isArray(challan.material_requests_data) && challan.material_requests_data.map((mat, idx) => (
                      <p key={idx} className="text-xs text-gray-600">
                        • {typeof mat.material_type === 'string' ? mat.material_type : 'N/A'}: {mat.weight_grams || mat.weight_carats || 0}{mat.weight_grams ? 'g' : 'ct'}
                        - ₹{typeof mat.insurance_value === 'number' ? mat.insurance_value.toLocaleString() : '0'}
                      </p>
                    ))}
                    <p className="text-sm font-bold text-teal-700 mt-2">
                      Total Value: ₹{typeof challan.total_approximate_value === 'number' ? challan.total_approximate_value.toLocaleString() : '0'}
                    </p>
                  </div>

                  {/* Transport Details */}
                  <div className="text-xs text-gray-600 mb-3">
                    <p>🚚 Transporter: {challan.transport_details?.transporter_name || 'N/A'}</p>
                    {challan.transport_details?.vehicle_number && (
                      <p>🚗 Vehicle: {challan.transport_details.vehicle_number}</p>
                    )}
                    {challan.transport_details?.lr_awb_number && (
                      <p>📋 LR/AWB: {challan.transport_details.lr_awb_number}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        try {
                          generateDeliveryChallanPDF(challan);
                          toast({
                            title: 'PDF Generated!',
                            description: 'Delivery challan downloaded successfully',
                          });
                        } catch (error) {
                          console.error('PDF generation error:', error);
                          toast({
                            title: 'Error',
                            description: error.message || 'Failed to generate PDF',
                            variant: 'destructive',
                          });
                        }
                      }}
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print PDF
                    </Button>

                    {challan.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => updateChallanStatus(challan.id, 'dispatched')}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        Mark Dispatched
                      </Button>
                    )}

                    {challan.status === 'dispatched' && (
                      <Button
                        size="sm"
                        onClick={() => updateChallanStatus(challan.id, 'received')}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        Mark Received
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create Challan Dialog */}
      <Dialog open={createChallanDialogOpen} onOpenChange={setCreateChallanDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Delivery Challan for {selectedWorkerGroup?.worker_name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Worker Info - LOCKED or SELECTION */}
            {selectedWorkerGroup?.is_unassigned ? (
              /* Unassigned - Show Worker Selection */
              <div className="bg-orange-100 border-2 border-orange-400 p-4 rounded-lg">
                <p className="font-semibold text-base text-orange-900 mb-3 flex items-center">
                  <span className="text-xl mr-2">⚠️</span>
                  Worker Not Assigned - Please Select
                </p>
                <Label className="text-base">Select Worker (Receiver) *</Label>
                <Select value={manualWorkerSelection || ''} onValueChange={setManualWorkerSelection}>
                  <SelectTrigger className="text-base py-6 mt-2">
                    <SelectValue placeholder="Select worker to receive materials" />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.map((worker) => (
                      <SelectItem key={worker.id} value={worker.id} className="text-base">
                        {worker.username} {worker.mobile_number && `(${worker.mobile_number})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-orange-700 mt-2">
                  These materials were created before worker assignment was mandatory. Please select which worker should receive them.
                </p>
              </div>
            ) : (
              /* Assigned Worker - LOCKED */
              <div className="bg-teal-100 border-2 border-teal-400 p-4 rounded-lg">
                <p className="font-semibold text-base text-teal-900 flex items-center">
                  <span className="text-xl mr-2">🔒</span>
                  Receiver (Job Worker): {selectedWorkerGroup?.worker_name}
                </p>
                {selectedWorkerGroup?.worker_mobile && (
                  <p className="text-sm text-teal-700 mt-1">
                    📱 Mobile: {selectedWorkerGroup.worker_mobile}
                  </p>
                )}
                <p className="text-xs text-teal-600 mt-2">
                  ⚠️ Worker assigned by supervisor - locked
                </p>
              </div>
            )}

            {/* Selected Materials Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-semibold text-base mb-3">Materials in this Challan ({selectedMaterialRequests.length}):</p>
              {selectedWorkerGroup?.requests
                .filter(req => selectedMaterialRequests.includes(req.id))
                .map((req) => {
                  const insurance = req.partial_issuances?.reduce((total, iss) =>
                    iss.needs_parcel && iss.insurance_value ? total + iss.insurance_value : total, 0) || 0;

                  return (
                    <div key={req.id} className="bg-white p-3 rounded border mb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">
                            {typeof req.material_type === 'string' ? req.material_type.toUpperCase() : 'N/A'}
                            {req.metal_purity && ` (${req.metal_purity})`}
                          </p>
                          <p className="text-xs text-gray-600">
                            Order: {typeof req.order_details?.order_number === 'string'
                              ? req.order_details.order_number
                              : 'N/A'} | {req.request_number || 'N/A'}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-teal-700">
                          ₹{typeof insurance === 'number' ? insurance.toLocaleString() : '0'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              <div className="border-t-2 border-teal-200 pt-3 mt-3">
                <p className="text-base font-bold text-teal-900">
                  Total Value: ₹{calculateSelectedTotal().toLocaleString()}
                </p>
              </div>
            </div>

            {/* Transport Details */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-base mb-3">🚚 Transport Details</h3>

              <div className="space-y-4">
                <div>
                  <Label className="text-base">Transporter Name *</Label>
                  <Select value={transporter} onValueChange={setTransporter}>
                    <SelectTrigger className="text-base py-6 mt-2">
                      <SelectValue placeholder="Select transporter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Air parcel" className="text-base">Air parcel</SelectItem>
                      <SelectItem value="Sequel" className="text-base">Sequel</SelectItem>
                      <SelectItem value="Jai ambe" className="text-base">Jai ambe</SelectItem>
                      <SelectItem value="Jai matha di" className="text-base">Jai matha di</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-base">Vehicle Number</Label>
                    <Input
                      value={vehicleNumber}
                      onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                      placeholder="e.g., MH12AB1234"
                      className="text-base py-6 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-base">LR / AWB Number</Label>
                    <Input
                      value={lrAwbNumber}
                      onChange={(e) => setLrAwbNumber(e.target.value)}
                      placeholder="e.g., LR123456"
                      className="text-base py-6 mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-base">E-Way Bill Number</Label>
                  <Input
                    value={ewayBillNumber}
                    onChange={(e) => setEwayBillNumber(e.target.value)}
                    placeholder="Optional"
                    className="text-base py-6 mt-2"
                  />
                </div>

                <div>
                  <Label className="text-base">Notes</Label>
                  <textarea
                    value={challanNotes}
                    onChange={(e) => setChallanNotes(e.target.value)}
                    placeholder="Any special instructions..."
                    rows={2}
                    className="w-full text-base p-3 mt-2 border rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateChallanDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateChallan}
              disabled={loading || selectedMaterialRequests.length === 0 || !transporter}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              Create Challan ({selectedMaterialRequests.length} materials)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParcelManagement;
